/**
 * Who GA4 and Clarity think this visitor is.
 *
 * Until this existed every behavioral event the site collected was anonymous:
 * `gtag()` was never called with a `user_id` and Clarity was never
 * `identify()`-ed. The event taxonomy was fine — `paywall_hit` reporting from
 * inside the modals, `purchase` carrying Stripe's own amount — but none of it
 * could be tied to an account, so "which gate did this subscriber hit before
 * they bought" and "is this subscriber dormant" had no answer at any price.
 * A CSV export existed only as an anonymous `map_conversion` event.
 *
 * **Only the Supabase user UUID ever leaves this module.** Never an email, a
 * display name, or anything else a person could be recognized by: GA4's terms
 * prohibit PII in `user_id`, and the UUID is already the join key every
 * internal table uses, so it is both the safe choice and the useful one.
 */

import type { Plan } from "./entitlements";

/**
 * Plan as the analytics tools see it.
 *
 * `signed_out` is deliberately distinct from `free`. A visitor who has never
 * made an account and a visitor who made one and stayed on the free tier are
 * at completely different points in the funnel, and collapsing them into
 * "free" would hide the activation step — which, at 34.5% of accounts ever
 * running a statewide search, is where the largest drop-off currently sits.
 */
export type AnalyticsPlan = Plan | "signed_out";

interface Identity {
  /** Supabase user UUID, or null when signed out. */
  userId: string | null;
  /**
   * Null means *not yet known* rather than "free" — the profile row loads
   * after the session does, and stamping a paying customer's first events
   * with `free` for that half-second would quietly corrupt exactly the
   * comparison this exists to enable.
   */
  plan: AnalyticsPlan | null;
}

/**
 * How long to keep trying to hand the identity to a tag that has not loaded.
 *
 * Neither tag is on the page when React first runs. `gtag` is defined
 * synchronously by the inline stub in index.html so it is effectively always
 * ready. Clarity's stub is now defined synchronously too, but it is
 * consent-gated (see index.html), and it used to wait up to ~5s for a CMP
 * before loading. Identifying once at sign-in would then land on
 * `window.clarity === undefined` and be lost; the poll keeps that from
 * mattering if a consent platform is ever added back.
 */
const POLL_INTERVAL_MS = 500;
const MAX_WAIT_MS = 20_000;

let desired: Identity = { userId: null, plan: null };
let gaApplied: Identity | null = null;
let clarityApplied: Identity | null = null;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let waitedMs = 0;

function sameIdentity(a: Identity | null, b: Identity): boolean {
  return a !== null && a.userId === b.userId && a.plan === b.plan;
}

function applyToGa(next: Identity): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;

  // `set` rather than a second `config` call: re-configuring the measurement
  // ID sends another page_view, which would double-count every sign-in.
  window.gtag("set", { user_id: next.userId });

  // `user_plan`, not `plan`: `paywall_hit` already sends an event parameter
  // called `plan` meaning "the plan being *sold*", and GA4 would offer two
  // unrelated dimensions under one label in every report picker. The name is
  // baked into the registered custom dimension, so this is expensive to undo.
  //
  // User-scoped, so it attaches to every subsequent event without each call
  // site having to remember it — and it lands in the BigQuery export under
  // `user_properties`, which is what the per-plan cohort queries read.
  if (next.plan !== null) {
    window.gtag("set", "user_properties", { user_plan: next.plan });
  }
}

function applyToClarity(next: Identity): void {
  if (typeof window === "undefined" || typeof window.clarity !== "function") return;

  // Clarity has no un-identify, so sign-out only re-tags the session; the
  // replay already recorded stays attributed to whoever was signed in when it
  // started. Clarity sessions are short (~30 min idle), so this self-corrects,
  // but it is worth knowing before reading a shared-device replay.
  if (next.userId) {
    window.clarity("identify", next.userId);
  }

  if (next.plan !== null) {
    window.clarity("set", "user_plan", next.plan);
  }
}

/**
 * Push the wanted identity into whichever tags are ready, and keep waiting for
 * the ones that are not.
 */
function flush(): void {
  if (typeof window === "undefined") return;

  const gaReady = typeof window.gtag === "function";
  const clarityReady = typeof window.clarity === "function";

  if (gaReady && !sameIdentity(gaApplied, desired)) {
    applyToGa(desired);
    gaApplied = { ...desired };
  }

  if (clarityReady && !sameIdentity(clarityApplied, desired)) {
    applyToClarity(desired);
    clarityApplied = { ...desired };
  }

  const settled = sameIdentity(gaApplied, desired) && sameIdentity(clarityApplied, desired);
  if (!settled && waitedMs < MAX_WAIT_MS) schedule();
}

function schedule(): void {
  if (pollTimer !== null || typeof window === "undefined") return;

  pollTimer = setTimeout(() => {
    pollTimer = null;
    waitedMs += POLL_INTERVAL_MS;
    flush();
  }, POLL_INTERVAL_MS);
}

/** Restart the wait window — a sign-in an hour into a session must not be
 *  dropped because the initial poll gave up long ago. */
function restartWait(): void {
  waitedMs = 0;
  flush();
}

/**
 * Tell GA4 and Clarity who this is.
 *
 * Safe to call repeatedly: unchanged identities are dropped rather than
 * re-sent, which matters because the auth effect that drives this re-runs
 * whenever the profile object's reference changes.
 *
 * @param userId Supabase user UUID. Never an email.
 * @param plan   Omit while the profile is still loading; call again with it
 *               once `resolvePlan` can answer.
 */
export function identifyUser(userId: string, plan?: Plan): void {
  const next: Identity = { userId, plan: plan ?? desired.plan };
  if (sameIdentity(desired, next) && sameIdentity(gaApplied, next)) return;

  desired = next;
  restartWait();
}

/** Sign-out. `plan` becomes `signed_out` rather than `free` — see AnalyticsPlan. */
export function clearUserIdentity(): void {
  const next: Identity = { userId: null, plan: "signed_out" };
  if (sameIdentity(desired, next) && sameIdentity(gaApplied, next)) return;

  desired = next;
  restartWait();
}

let internalMarked = false;

/**
 * Mark everything this page sends from now on as internal traffic — the
 * owner's own account, or a test one (`analytics_internal_users`).
 *
 * GA4 gets `traffic_type: 'internal'`, the parameter its built-in "Internal
 * Traffic" data filter matches. Leave that filter in *Testing*: an *Active*
 * filter deletes the matching events for good, from GA4 and from the BigQuery
 * export alike, and nothing can bring them back. In Testing they are only
 * labelled, and reports exclude them with a comparison on "Test data filter
 * name".
 *
 * Deliberately sticky for the life of the page, including after sign-out:
 * whoever is at this keyboard is still the owner. The page view that fired
 * before the account was known is not covered — BigQuery can exclude that too,
 * by dropping every `user_pseudo_id` that ever carried an internal `user_id`.
 */
export function markInternalTraffic(): void {
  if (internalMarked || typeof window === "undefined") return;
  internalMarked = true;

  // `gtag` is the index.html stub, defined before any of this runs.
  if (typeof window.gtag === "function") window.gtag("set", { traffic_type: "internal" });

  // Clarity may not have loaded yet (it waits on consent); poll briefly, as
  // identify does, rather than lose the tag.
  let waited = 0;
  const tagClarity = () => {
    if (typeof window.clarity === "function") {
      window.clarity("set", "internal", "true");
    } else if ((waited += POLL_INTERVAL_MS) <= MAX_WAIT_MS) {
      setTimeout(tagClarity, POLL_INTERVAL_MS);
    }
  };
  tagClarity();
}

/**
 * Tag the current Clarity session so a group of replays is findable.
 *
 * Clarity accumulates repeated values for one key across a session, so a
 * visitor who hits two different gates carries both. This is the difference
 * between "we know 40 people hit the CSV paywall" and being able to watch
 * what those 40 people did next.
 */
export function tagSession(key: string, value: string): void {
  if (typeof window === "undefined" || typeof window.clarity !== "function") return;
  window.clarity("set", key, value);
}

/** Reset module state. Tests only. */
export function __resetIdentityForTests(): void {
  internalMarked = false;
  desired = { userId: null, plan: null };
  gaApplied = null;
  clarityApplied = null;
  waitedMs = 0;
  if (pollTimer !== null) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}
